import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { TrendingDown, FileText, Shield, CheckCircle, Phone, Mail, MapPin } from 'lucide-react';
import { mockFormSubmission } from '../utils/mock';
import { toast } from '../hooks/use-toast';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    totalFundings: '',
    totalDebt: '',
    dailyPaymentsCount: '',
    dailyAmount: '',
    weeklyPaymentsCount: '',
    weeklyAmount: ''
  });
  const [loading, setLoading] = useState(false);
  const [agreeToContact, setAgreeToContact] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!agreeToContact) {
      toast({
        title: "Agreement Required",
        description: "Please agree to be contacted before submitting the form.",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    
    try {
      await mockFormSubmission(formData);
      toast({
        title: "Success!",
        description: "Your request has been submitted. We'll contact you within 24 hours.",
      });
      setFormData({ 
        name: '', 
        email: '', 
        phone: '', 
        company: '', 
        message: '',
        totalFundings: '',
        totalDebt: '',
        dailyPaymentsCount: '',
        dailyAmount: '',
        weeklyPaymentsCount: '',
        weeklyAmount: ''
      });
      setAgreeToContact(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation Header */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_fresh-site-7/artifacts/q4wqp47g_Screenshot%202025-10-29%20at%2010.27.34%E2%80%AFPM.png" 
                alt="Guardian Advisory Group Logo" 
                className="h-10 w-auto"
              />
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-slate-600 hover:text-emerald-600 transition-colors font-medium">
                Home
              </button>
              <button onClick={() => scrollToSection('benefits')} className="text-slate-600 hover:text-emerald-600 transition-colors font-medium">
                Benefits
              </button>
              <button onClick={() => scrollToSection('how-it-works')} className="text-slate-600 hover:text-emerald-600 transition-colors font-medium">
                How It Works
              </button>
              <button onClick={() => scrollToSection('success-stories')} className="text-slate-600 hover:text-emerald-600 transition-colors font-medium">
                Success Stories
              </button>
              <button onClick={() => scrollToSection('faq')} className="text-slate-600 hover:text-emerald-600 transition-colors font-medium">
                FAQ
              </button>
              <a href="tel:8668853277" className="flex items-center text-slate-600 hover:text-emerald-600 transition-colors font-medium">
                <Phone className="w-4 h-4 mr-2" />
                (866) 885-3277
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative overflow-hidden bg-gradient-to-br from-emerald-600 via-teal-600 to-blue-700 text-white">
        <div className="container mx-auto px-6 py-24 md:py-32 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Break Free from Daily and Weekly Advances
            </h1>
            <p className="text-xl md:text-2xl mb-10 text-emerald-50 leading-relaxed">
              Guardian Advisory Group helps small businesses streamline existing funding programs into one manageable monthly plan — improving cash flow and giving your business the breathing room it needs to grow.
            </p>
            <Button 
              onClick={scrollToContact}
              size="lg" 
              className="bg-white text-emerald-700 hover:bg-emerald-50 text-lg px-8 py-6 rounded-lg shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              Get Your Free Payment Review
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">How We Help Your Business</h2>
            <p className="text-lg text-slate-600">Tailored solutions to restore financial stability</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <CardHeader>
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center mb-4">
                  <TrendingDown className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-2xl text-slate-800">Reduce Monthly Payments</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-slate-600 leading-relaxed">
                  Our advisors collaborate with your funding partners to identify opportunities for improved terms and reduce the pressure of frequent remits — helping you move toward one consistent monthly payment that aligns with your business's cash-flow cycle.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <CardHeader>
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center mb-4">
                  <FileText className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-2xl text-slate-800">Consolidate Multiple Fundings</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-slate-600 leading-relaxed">
                  Combine several existing business funding programs into a single, simplified payment structure — reducing administrative stress and helping your business operate more efficiently.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <CardHeader>
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="text-2xl text-slate-800">Protect Your Business Relationships</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-slate-600 leading-relaxed">
                  Our team coordinates with funders and service providers to ensure clear communication and a smoother process — allowing you to focus on running and growing your business.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-6 bg-gradient-to-b from-slate-50 to-emerald-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">How It Works</h2>
            <p className="text-lg text-slate-600">A simple, proven process to restore your cash flow</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="relative flex">
              <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col h-full w-full">
                <div className="text-5xl font-bold text-emerald-200 mb-4">01</div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">Free Consultation</h3>
                <p className="text-slate-600 leading-relaxed flex-grow">
                  We review your current multiple funding positions, analyze your cash flow, and assess your business objectives to understand your unique situation.
                </p>
              </div>
            </div>

            <div className="relative flex">
              <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col h-full w-full">
                <div className="text-5xl font-bold text-teal-200 mb-4">02</div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">Custom Strategy</h3>
                <p className="text-slate-600 leading-relaxed flex-grow">
                  Our team develops a tailored restructuring plan designed to reduce your daily or weekly payments and stabilize your business's cash flow.
                </p>
              </div>
            </div>

            <div className="relative flex">
              <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col h-full w-full">
                <div className="text-5xl font-bold text-blue-200 mb-4">03</div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">Restructure</h3>
                <p className="text-slate-600 leading-relaxed flex-grow">
                  We work directly with your funding providers to coordinate improved terms, adjust payment schedules, and implement a sustainable repayment structure that supports your business's cash flow.
                </p>
              </div>
            </div>

            <div className="relative flex">
              <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col h-full w-full">
                <div className="text-5xl font-bold text-cyan-200 mb-4">04</div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">Resolution</h3>
                <p className="text-slate-600 leading-relaxed flex-grow">
                  Once new agreements are finalized, we help implement your revised payment structure and provide continued support to keep your plan on track.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={scrollToContact}
              size="lg" 
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-8 py-6 text-lg rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105"
            >
              Start Your Free Review
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="success-stories" className="py-20 px-6 relative overflow-hidden">
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">Client Success Stories</h2>
            <p className="text-lg text-slate-600">Real results for real businesses</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="flex items-center mb-4">
                  <CheckCircle className="w-6 h-6 text-emerald-600 mr-2" />
                  <CardTitle className="text-lg text-slate-800">Sarah J.</CardTitle>
                </div>
                <CardDescription className="text-sm text-slate-500">Restaurant Owner, Chicago</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 leading-relaxed italic">
                  "We had three advances with daily payments exceeding $5,000. Guardian advised us on how to consolidate them into one manageable monthly plan that eased our cash-flow pressure. We finally have breathing room again."
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="flex items-center mb-4">
                  <CheckCircle className="w-6 h-6 text-teal-600 mr-2" />
                  <CardTitle className="text-lg text-slate-800">Michael C.</CardTitle>
                </div>
                <CardDescription className="text-sm text-slate-500">Retail Business, Los Angeles</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 leading-relaxed italic">
                  "Within weeks, Guardian advised us on how to secure new terms that reduced our weekly payments and stabilized our cash flow. Their guidance gave us the confidence to focus on growth again."
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="flex items-center mb-4">
                  <CheckCircle className="w-6 h-6 text-blue-600 mr-2" />
                  <CardTitle className="text-lg text-slate-800">David M.</CardTitle>
                </div>
                <CardDescription className="text-sm text-slate-500">Construction Company, Miami</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 leading-relaxed italic">
                  "Guardian helped us combine multiple funding positions into one manageable program. The change gave us the stability we needed to keep operations running smoothly."
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6 bg-slate-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-slate-600">Get answers to common questions about our services</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-white rounded-lg shadow px-6">
              <AccordionTrigger className="text-left text-slate-800 hover:text-emerald-600">
                How quickly can you help restructure my funding payments?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                Most clients see initial results within 2-4 weeks of starting the consultation process. The timeline depends on your specific situation, the number of funding providers involved, and the complexity of your existing agreements. We work efficiently to provide relief as quickly as possible while ensuring the best long-term outcomes for your business.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-white rounded-lg shadow px-6">
              <AccordionTrigger className="text-left text-slate-800 hover:text-emerald-600">
                Will this affect my business credit?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                Our advisory services focus on helping businesses work with their existing funding providers to improve cash flow management. Since most business funding programs are not reported to business credit bureaus, adjustments to your payment schedule typically do not impact your business credit score. We focus on maintaining positive relationships with your funders while helping you strengthen your financial stability.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-white rounded-lg shadow px-6">
              <AccordionTrigger className="text-left text-slate-800 hover:text-emerald-600">
                What if I have multiple fundings with different providers?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                This is one of our core specialties. Many of our clients work with several funding providers at once, which can create complex payment schedules. Guardian helps organize and streamline these into one simplified plan, reducing administrative stress and improving cash-flow consistency. Our experience with diverse funding providers allows us to guide clients through these situations effectively.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-white rounded-lg shadow px-6">
              <AccordionTrigger className="text-left text-slate-800 hover:text-emerald-600">
                How much does the service cost?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                We offer a free initial consultation and payment review to assess your situation. Our fee structure is transparent and based on the complexity of your program and level of advisory support. All costs are discussed upfront before you decide to move forward. Many clients find that the cash-flow improvements achieved through our process more than justify the investment in our services.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-white rounded-lg shadow px-6">
              <AccordionTrigger className="text-left text-slate-800 hover:text-emerald-600">
                Can you assist with daily ACH withdrawals?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                Yes, absolutely. Daily ACH withdrawals can create significant strain on cash flow for many business owners. Our advisors help review your payment schedules and explore options to create a more manageable structure — typically transitioning from high-frequency withdrawals to a smoother monthly plan that better aligns with your revenue cycles.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="bg-white rounded-lg shadow px-6">
              <AccordionTrigger className="text-left text-slate-800 hover:text-emerald-600">
                What documents do I need to get started?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                To begin, we'll review copies of your existing funding agreements, recent bank statements (typically 3–6 months), and basic business information. During your initial consultation, we'll provide a tailored checklist for your situation. If you don't have everything immediately, no problem—we'll guide you through each step.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-gradient-to-br from-emerald-600 via-teal-600 to-blue-700 text-white relative overflow-hidden">
        <div className="container mx-auto max-w-5xl relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Get Your Free Payment Evaluation Today</h2>
            <p className="text-xl text-emerald-50 leading-relaxed max-w-3xl mx-auto">
              Take the first step toward better business cash flow. Our advisors will review your current funding arrangements, outline available program improvement options, and design a plan that fits your business — all at no cost and with no obligation.
            </p>
          </div>

          <Card className="max-w-2xl mx-auto border-none shadow-2xl">
            <CardContent className="pt-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700">Full Name *</Label>
                    <Input 
                      id="name" 
                      name="name" 
                      value={formData.name}
                      onChange={handleInputChange}
                      required 
                      placeholder="John Smith"
                      className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700">Email Address *</Label>
                    <Input 
                      id="email" 
                      name="email" 
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required 
                      placeholder="john@business.com"
                      className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700">Phone Number *</Label>
                    <Input 
                      id="phone" 
                      name="phone" 
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required 
                      placeholder="(555) 123-4567"
                      className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-slate-700">Company Name *</Label>
                    <Input 
                      id="company" 
                      name="company" 
                      value={formData.company}
                      onChange={handleInputChange}
                      required 
                      placeholder="ABC Business Inc."
                      className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-700">Tell us about your situation</Label>
                  <Textarea 
                    id="message" 
                    name="message" 
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Please describe your current funding situation, number of advances, and any immediate concerns..."
                    className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500 resize-none"
                  />
                </div>

                {/* Funding Questions Section */}
                <div className="border-t border-slate-200 pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">Funding Details</h3>
                  
                  {/* Field 1 - Total Active Fundings */}
                  <div className="space-y-2 mb-4">
                    <Label htmlFor="totalFundings" className="text-slate-700">How many active funding payments do you currently have? *</Label>
                    <Select value={formData.totalFundings} onValueChange={(value) => handleSelectChange('totalFundings', value)}>
                      <SelectTrigger className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500">
                        <SelectValue placeholder="Select number of fundings" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1</SelectItem>
                        <SelectItem value="2-3">2–3</SelectItem>
                        <SelectItem value="4-5">4–5</SelectItem>
                        <SelectItem value="6+">6+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Total Funding Debt */}
                  <div className="space-y-2 mb-4">
                    <Label htmlFor="totalDebt" className="text-slate-700">What is your approximate total funding debt?</Label>
                    <Select value={formData.totalDebt} onValueChange={(value) => handleSelectChange('totalDebt', value)}>
                      <SelectTrigger className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500">
                        <SelectValue placeholder="Select total debt range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="under-50k">Under $50,000</SelectItem>
                        <SelectItem value="50k-100k">$50,000–$100,000</SelectItem>
                        <SelectItem value="100k-250k">$100,000–$250,000</SelectItem>
                        <SelectItem value="250k-500k">$250,000–$500,000</SelectItem>
                        <SelectItem value="500k+">$500,000+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Field 2 - Daily Payments Count (Conditional) */}
                  {formData.totalFundings && formData.totalFundings !== '1' && (
                    <div className="space-y-2 mb-4">
                      <Label htmlFor="dailyPaymentsCount" className="text-slate-700">How many of those payments are daily?</Label>
                      <Select value={formData.dailyPaymentsCount} onValueChange={(value) => handleSelectChange('dailyPaymentsCount', value)}>
                        <SelectTrigger className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500">
                          <SelectValue placeholder="Select daily payments count" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="1">1</SelectItem>
                          <SelectItem value="2">2</SelectItem>
                          <SelectItem value="3+">3+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Field 3 - Total Daily Amount */}
                  <div className="space-y-2 mb-4">
                    <Label htmlFor="dailyAmount" className="text-slate-700">Approximate combined daily payments</Label>
                    <Select value={formData.dailyAmount} onValueChange={(value) => handleSelectChange('dailyAmount', value)}>
                      <SelectTrigger className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500">
                        <SelectValue placeholder="Select daily payment amount" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="under-250">Under $250/day</SelectItem>
                        <SelectItem value="250-750">$250–$750/day</SelectItem>
                        <SelectItem value="750-1500">$750–$1,500/day</SelectItem>
                        <SelectItem value="1500+">$1,500+/day</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Field 4 - Weekly Payments Count */}
                  <div className="space-y-2 mb-4">
                    <Label htmlFor="weeklyPaymentsCount" className="text-slate-700">How many of those payments are weekly?</Label>
                    <Select value={formData.weeklyPaymentsCount} onValueChange={(value) => handleSelectChange('weeklyPaymentsCount', value)}>
                      <SelectTrigger className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500">
                        <SelectValue placeholder="Select weekly payments count" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="1">1</SelectItem>
                        <SelectItem value="2">2</SelectItem>
                        <SelectItem value="3+">3+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Field 5 - Total Weekly Amount */}
                  <div className="space-y-2 mb-4">
                    <Label htmlFor="weeklyAmount" className="text-slate-700">Approximate combined weekly payments</Label>
                    <Select value={formData.weeklyAmount} onValueChange={(value) => handleSelectChange('weeklyAmount', value)}>
                      <SelectTrigger className="border-slate-300 focus:border-emerald-500 focus:ring-emerald-500">
                        <SelectValue placeholder="Select weekly payment amount" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="under-1500">Under $1,500/week</SelectItem>
                        <SelectItem value="1500-3500">$1,500–$3,500/week</SelectItem>
                        <SelectItem value="3500-7500">$3,500–$7,500/week</SelectItem>
                        <SelectItem value="7500+">$7,500+/week</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-start space-x-3 pt-2">
                  <Checkbox 
                    id="agree" 
                    checked={agreeToContact}
                    onCheckedChange={setAgreeToContact}
                    className="mt-1"
                  />
                  <label htmlFor="agree" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
                    By submitting this form, you agree to be contacted by Guardian Advisory Group via email, phone, and text message regarding your business cash-flow and program review needs.
                  </label>
                </div>

                <Button 
                  type="submit" 
                  disabled={loading || !agreeToContact}
                  size="lg" 
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white py-6 text-lg rounded-lg shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Submitting...' : 'Start My Evaluation'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-3 gap-8 mt-16 max-w-3xl mx-auto">
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Phone className="w-6 h-6" />
              </div>
              <p className="text-emerald-50">Call us anytime</p>
              <a href="tel:8668853277" className="font-semibold hover:underline">(866) 885-3277</a>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Mail className="w-6 h-6" />
              </div>
              <p className="text-emerald-50">Email us</p>
              <a href="mailto:help@guardianadvisorygrp.com" className="font-semibold hover:underline">help@guardianadvisorygrp.com</a>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <MapPin className="w-6 h-6" />
              </div>
              <p className="text-emerald-50">Visit us</p>
              <p className="font-semibold">New York, NY</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-white mb-4">Guardian Advisory Group</h3>
            <p className="text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Helping small businesses achieve financial stability through expert cash-flow restructuring and advisory services.
            </p>
          </div>
          
          <div className="border-t border-slate-700 pt-8">
            <p className="text-sm text-slate-400 text-center leading-relaxed">
              <strong className="text-slate-300">Disclaimer:</strong> Guardian Advisory Group offers business-to-business funding and cash-flow consulting services exclusively for small-business clients. We are not a consumer debt-relief, credit-repair, or lending company.
            </p>
            <p className="text-sm text-slate-500 text-center mt-4">
              © Guardian Advisory Group. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
